import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client'; // Import supabase client

const AdminRegister: React.FC = () => {
  const [email, setEmail] = useState(''); // Changed username to email
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email.trim() || !password.trim() || !fullName.trim()) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    // Supabase password minimum length is 6 by default
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    console.log(`REG: Attempting Supabase signUp for email: ${email}`);

    try {
      // Call Supabase signUp
      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          // Store full name in user_metadata
          data: {
            full_name: fullName,
          }
        }
      });

      if (error) {
        console.error('REG: Supabase signUp error:', error);
        toast.error(`Erro ao registrar: ${error.message}`);
        setIsLoading(false);
        return;
      }

      console.log('REG: Supabase signUp successful:', data);

      // Check if user object exists and email confirmation might be needed
      if (data.user && !data.session) {
          // NOTE: We disabled email confirmation earlier, but Supabase might still return
          // user data without a session immediately after signup in some flows.
          // If confirmation was enabled, we would show a message here.
          toast.warning('Registro realizado! Verifique seu email se a confirmação estiver ativa.');
          console.log('REG: SignUp successful, but no session returned immediately (may require confirmation or login).');
      } else if (data.user && data.session) {
         console.log('REG: SignUp successful and session created.');
         toast.success('Administrador cadastrado com sucesso!');
      } else {
        console.log('REG: SignUp response structure unexpected:', data);
        toast.info('Registro processado. Tente fazer login.');
      }

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate('/admin/login');
      }, 1500);

    } catch (error: any) {
      console.error('REG: Exception during Supabase signUp:', error);
      toast.error(`Erro inesperado ao registrar: ${error.message || 'Erro desconhecido'}`);
    } finally {
      // Ensure loading is set to false even if redirect happens quickly
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold">Aqua Cart Navigator</h1>
        </div>

        <h2 className="text-xl font-bold text-center">Cadastrar Novo Administrador</h2>

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="fullName">
              Nome Completo
            </label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Digite seu nome completo"
              required
            />
          </div>

          {/* Changed to Email */}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email" // Use email type
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Digite seu email"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="password">
              Senha
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha (mínimo 6 caracteres)"
              required
              autoComplete="new-password"
            />
             <p className="text-xs text-gray-500">A senha deve ter pelo menos 6 caracteres</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="confirmPassword">
              Confirmar Senha
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirme sua senha"
              required
              autoComplete="new-password"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Cadastrando...' : 'Cadastrar'}
          </Button>

          <div className="text-center mt-4">
            <Link to="/admin/login" className="text-blue-500 hover:underline">
              Já tem uma conta? Faça login
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AdminRegister;
